import React, { useEffect, useState } from 'react';
import { IModalData, ModalComponent, PasslyButton, PasslyTable } from '@app/components';
import { vaultPasswordsService, basicService } from '@app/services';
import {
    infiniteScrollItemsFetchLimit,
    getPasswordTypeUIDetails,
    interpolateTranslation,
    communityPassword,
    snmpAuthPassword,
    genericErrorToast,
} from '@app/helpers';
import {
    IVaultPasswordDTO,
    Order,
    OrderType,
    IPasswordTypeUIDetails,
    VaultPasswordExpireReason,
    IDotMenu,
    IActiveFilter, 
    FilterEnum,
    Intercom,
    ITableHead
} from '@app/models';
import { TableCell, IconButton, Typography, TableHead } from '@material-ui/core';
import { PasslyTooltip } from '@app/componentsV2';
import VaultPasswordComponent from './vaultPassword.component';
import { Warning } from '@material-ui/icons';
import { vaultAdd, vaultAdmin, vaultDelete, vaultUpdate } from '@app/store/constants';
import { confirm } from '@app/components/Modal/confirmModal';
import { toast } from 'react-toastify';
import { useTypedTranslation } from '@app/hooks';
import { UpdatePasswordDialog } from '../dialogs/updatePassword.dialog';
import { useSelector, useDispatch } from 'react-redux';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import Icon from '@app/components/Icon';
import { AxiosError } from 'axios';
import { resetScroll } from '@app/store/actions';

interface IProps {
    vaultId: string;
    isIndividual: boolean;
    vaultName: string;
    folderId: string;
    openPasswordCallback: Function;
    foldersLoading: boolean;
    permissions: Set<string>;
    querySharedLink: boolean;
    queryPasswordId: string;
    queryPasswordName: string;
}

interface IVaultPasswordRow extends IVaultPasswordDTO {
    DisplayType: string;
    Source: string;
    Checked: boolean;
    checked: boolean;
    IsCompromised: boolean;
    id: string;
    cutId: string;
}

enum TableHeadEnum {
    id = 'id',
    displayName = 'DisplayName',
    passwordType = 'PasswordType',
    isCompromised = 'IsCompromised',
    triggerExpirationButton = 'triggerExpirationButton',
    infoButton = 'infoButton'
}

const PasswordListComponent: React.SFC<IProps> = ({
    vaultName,
    vaultId,
    isIndividual,
    folderId,
    openPasswordCallback,
    foldersLoading,
    permissions,
    querySharedLink,
    queryPasswordId,
    queryPasswordName
}) => {
    const [rows, setRows] = useState<IVaultPasswordRow[]>([]);
    const [passwordsCount, setPasswordsCount] = useState(0);
    const [reset, setReset] = useState(0);
    const [order, setOrder] = useState<OrderType>(Order.asc);
    const [filters, setFilters] = useState<IActiveFilter[]>([]);
    const [loading, setLoading] = useState(true);
    const [openUpdatePasswordModal, setUpdatePasswordModal] = useState(false);
    const [clickedExpirePasswords, setClickedExpirePasswords] = useState<string[]>([]);
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const intercom : Intercom = useSelector((state: any) => state.basic.shellConfig.intercom);

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    useEffect(() => {
        if (querySharedLink && queryPasswordName !== '' && queryPasswordId !== '' && !loading) {
            openPasswordCallback(queryPasswordId, vaultId, queryPasswordName, true);
        }
    }, [loading]);

    useEffect(() => {
        if (!foldersLoading) {
            setLoading(true);
            setRows([]);
            setReset(reset + 1);
            setPasswordsCount(0);
        }
    }, [foldersLoading, order, folderId, filters]);

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        if (folderId === '') {
            if (!foldersLoading) {
                setLoading(false);
            }
            return;
        }

        vaultPasswordsService
            .getVaultPasswordsDTO(vaultId, 'DisplayName', order, infiniteScrollItemsFetchLimit, skipCount, isReset, folderId, filters)
            .then((response: any) => {
                if (isReset) {
                    setPasswordsCount(response.data['@odata.count']);
                }

                const passwords: IVaultPasswordRow[] = response.data.value;

                passwords.forEach((password) => {
                    password.Checked = false;

                    const typeDetails: IPasswordTypeUIDetails = getPasswordTypeUIDetails(password.PasswordType);

                    password.DisplayType = translation(typeDetails.passwordTypeForDisplay);
                    password.Source = typeDetails.passwordTypeImage;
                    password.id = password.Id;
                    password.cutId = `...${password.Id.slice(-12)}`;
                });

                if (passwords.length === 0) {
                    setRows([]);
                } else {
                    setRows(rows.concat(passwords));
                }

                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const editPasswordClick = (id: string) => () => {
        basicService.redirect(
            `/passwordserver/vaults/${vaultId}/editpassword/${id}?vaultName=${encodeURIComponent(vaultName)}&isIndividual=${isIndividual}`
        );
    };

    const deletePasswordsDialog = (ids: string[]) => {
        confirm(
            translation('DELETE_VAULT_PASSWORD_BODY'),
            translation('DELETE'),
            translation('CANCEL'),
            translation('DELETE_VAULT_PASSWORD_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                deletePasswords(ids);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const deletePasswords = (ids: string[]) => {
        vaultPasswordsService.deleteVaultPasswords(ids, vaultId, handleDeletePasswordsSuccess.bind(null, ids), handleDeletePasswordsError);
    };

    const deleteRow = (e: any) => {
        let ids: string[] = [];
        ids.push(e.currentTarget.dataset.id);

        deletePasswordsDialog(ids);
    };
    const deleteSelected = () => {
        let ids: string[] = [];
        rows.forEach((row) => {
            if (row.checked) {
                ids.push(row.Id);
            }
        });

        deletePasswordsDialog(ids);
    };

    const handleDeletePasswordsSuccess = (ids: string[]) => {
        let filteredRows = rows.filter((row) => {
            return !ids.includes(row.Id);
        });

        setPasswordsCount(passwordsCount - ids.length);
        setRows(filteredRows);
    };

    const handleDeletePasswordsError = () => {
        toast(translation('DELETE_VAULT_PASSWORD_ERROR'));
    };

    const handleModalCloseCallback = (close?: boolean) => {
        if (close !== undefined) {
            setUpdatePasswordModal(false);
        } else {
            setUpdatePasswordModal(!openUpdatePasswordModal);
        }
    };

    const updatePassword = (id: string, passwordName: string, vaultId: string) => () => {
        setUpdatePasswordModal(true);
        setModalData({
            headerTitle: translation('UPDATE_PWD_FOR') + ' ' + passwordName,
            form: (
                <UpdatePasswordDialog
                    passwordId={id}
                    vaultId={vaultId}
                    handleRefresh={handlePasswordRefreshed}
                    dialogOpen={handleModalCloseCallback}
                />
            )
        });
    };

    const handlePasswordRefreshed = (refresh: boolean, passwordName: string) => {
        const args: string[] = [passwordName];
        const snackbarMessage: string = interpolateTranslation(translation('PASSWORD_CHANGED'), args);

        dispatch(handleSetSnackbar({ show: true, message: snackbarMessage, severity: 'success' }));
    };

    const resyncPassword = (vaultId: string, passwordId: string) => {
        let json: any = {};
        json.VaultPasswordId = passwordId;

        vaultPasswordsService.removeFailedSyncFlag(json, vaultId, handleSuccessResync.bind(null, passwordId), genericErrorToast);
    };

    const expirePasswordSuccess = (passwordId: string) => () => {
        setClickedExpirePasswords([...clickedExpirePasswords, passwordId]);
    };
    const expirePasswordError = (error: AxiosError<any>) => {
        genericErrorToast(error);
    };

    const handleExpirePassword = (vaultPasswordId: string, vaultId: string) => () => {
        vaultPasswordsService.expirePassword(
            vaultPasswordId,
            vaultId,
            VaultPasswordExpireReason.Compromised,
            expirePasswordSuccess(vaultPasswordId),
            expirePasswordError
        );
    };

    const handleSuccessResync = (passwordId: string) => {
        dispatch(
            handleSetSnackbar({
                show: true,
                message: translation('RESYNC_IN_PROGRESS'),
                severity: 'success'
            })
        );

        // remove retry button
        let newRows = [...rows];

        const rowChanged: IVaultPasswordRow[] = newRows.filter((row: IVaultPasswordRow) => {
            return row.Id === passwordId;
        });

        rowChanged[0].FailedSync = false;
        setRows(newRows);
    };

    const getComplexCellName = (row: IVaultPasswordRow) => {
        return permissions.has(vaultUpdate) ? (
            <TableCell size='small' onClick={editPasswordClick(row.Id)} className='vault__passwords--hover'>
                <PasslyTooltip title={row.DisplayName}>
                    <div className='vault__passwords'>
                        <div className={`vault__passwords-ico ${row.Source}`} title={row.DisplayType} />
                        <div className='vault__passwords--wrapper'>
                            <span className='vault__passwords--name'>{row.DisplayName}</span>
                            <span className='vault__passwords--description'>{row.Description}</span>
                        </div>
                    </div>
                </PasslyTooltip>
            </TableCell>
        ) : (
            <TableCell size='small' onClick={() => openPasswordCallback(row.Id, row.VaultId, row.DisplayName)}>
                <PasslyTooltip title={row.DisplayName}>
                    <div className='vault__passwords'>
                        <div className={`vault__passwords-ico ${row.Source}`} title={row.DisplayType} />
                        <div className='vault__passwords--wrapper'>
                            <span className='vault__passwords--name'>{row.DisplayName}</span>
                            <span className='vault__passwords--description'>{row.Description}</span>
                        </div>
                    </div>
                </PasslyTooltip>
            </TableCell>
        );
    };
    const getComplexCellCutId = (row: IVaultPasswordRow) => {
        return (
            <PasslyTooltip title={row.Id}>
                <TableCell
                    size='small'
                    onClick={
                        permissions.has(vaultUpdate) ? editPasswordClick(row.Id) : () => openPasswordCallback(row.Id, row.VaultId, row.DisplayName)
                    }
                    className={permissions.has(vaultUpdate) ? 'vault__passwords--hover' : ''}
                >
                    {row.cutId}
                </TableCell>
            </PasslyTooltip>
        );
    };
    const getComplexCellPasswordType = (row: IVaultPasswordRow) => {
        return (
            <TableCell>
                <div className='vault__passwords'>
                    {row.PasswordType === 'SNMP' && (
                        <VaultPasswordComponent vaultId={row.VaultId} passwordId={row.Id} updateable={false} passwordWanted={communityPassword} />
                    )}
                    {row.PasswordType === 'SNMPV3' && (
                        <VaultPasswordComponent vaultId={row.VaultId} passwordId={row.Id} updateable={false} passwordWanted={snmpAuthPassword} />
                    )}
                    {row.PasswordType !== 'SNMP' && row.PasswordType !== 'SNMPV3' && (
                        <VaultPasswordComponent vaultId={row.VaultId} passwordId={row.Id} updateable={false} />
                    )}
                    {permissions.has(vaultUpdate) && (
                        <PasslyTooltip title={translation('UPDATE_NOW')}>
                            <IconButton size='small' onClick={updatePassword(row.Id, row.DisplayName, row.VaultId)}>
                                <Icon
                                    icon='refreshPasswordIco'
                                    className='tablev2__generate-icon'
                                />
                            </IconButton>
                        </PasslyTooltip>
                    )}
                </div>
            </TableCell>
        );
    };
    const getComplexCellIsCompromised = (row: IVaultPasswordRow) => {
        return row.IsCompromised ? (
            <>
                <TableCell
                    className={`${
                        permissions.has(vaultAdmin) || permissions.has(vaultAdd) || permissions.has(vaultUpdate)
                            ? 'vault__passwords-cursor-default'
                            : ''
                    }`}
                >
                    {!clickedExpirePasswords.includes(row.Id) ? <Typography color='error'>{translation('COMPROMISED')}</Typography> : null}
                </TableCell>
            </>
        ) : (
            <TableCell />
        );
    };
    const getComplexCellTriggerExpiration = (row: IVaultPasswordRow) => {
        return row.IsCompromised ? (
            <>
                <TableCell className='vault__passwords-cursor-default'>
                    {(permissions.has(vaultAdmin) || permissions.has(vaultAdd) || permissions.has(vaultUpdate)) &&
                    !clickedExpirePasswords.includes(row.Id) ? (
                        <PasslyButton
                            id='expirePasswordButton'
                            variant='contained'
                            color='primary'
                            onClick={handleExpirePassword(row.Id, row.VaultId)}
                            disableRipple={true}
                            className='loginSelected__button-other'
                        >
                            {translation('EXPIRE_PASSWORD')}
                        </PasslyButton>
                    ) : null}
                </TableCell>
            </>
        ) : (
            <TableCell />
        );
    };

    const getComplexCellInformationalButton = (password: IVaultPasswordRow) => {
        if (password.FailedSync) {
            return (
                <TableCell>
                    <PasslyTooltip title={translation('SYNC_FAILED')}>
                        <span className='label__error' onClick={() => resyncPassword(password.VaultId, password.Id)}>
                            <Warning fontSize='small' />
                            {translation('RESYNC')}
                        </span>
                    </PasslyTooltip>
                </TableCell>
            );
        } else {
            return <TableCell />;
        }
    };

    const getHeadCells = () => {
        const cells = [] as ITableHead[];

        cells.push(
            {
                id: TableHeadEnum.id,
                label: 'PASSWORD_ID',
                hideSorting: true,
                width: '150px',
                type: 'getComplexCell',
                getComplexCell: getComplexCellCutId,
            },
            {
                id: TableHeadEnum.displayName,
                label: 'PASSWORD_NAME',
                type: 'getComplexCell',
                filterType: FilterEnum.contains,
                defaultSearchBy: TableHeadEnum.displayName,
                getComplexCell: getComplexCellName
            },
            {
                id: TableHeadEnum.passwordType,
                label: 'PASSWORD',
                type: 'getComplexCell',
                hideSorting: true,
                getComplexCell: getComplexCellPasswordType
            }
        );

        if (intercom.dwidEnabled)
        {
            cells.push(
                {
                    id: TableHeadEnum.isCompromised,
                    label: 'COMPROMISED_PASSWORD',
                    type: 'getComplexCell',
                    hideSorting: true,
                    getComplexCell: getComplexCellIsCompromised,
                }
            );
        }

        cells.push(
            {
                id: TableHeadEnum.triggerExpirationButton,
                label: 'TRIGGER_EXPIRATION',
                type: 'getComplexCell',
                hideSorting: true,
                getComplexCell: getComplexCellTriggerExpiration
            },
            {
                id: TableHeadEnum.infoButton,
                label: '',
                type: 'getComplexCell',
                hideSorting: true,
                width: '150px',
                getComplexCell: getComplexCellInformationalButton
            }
        )

        return cells;
    }
    const headCells = getHeadCells();

    const createSortHandler = () => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;

        setOrder(ascDesc);
        dispatch(resetScroll());
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('DELETE_SELECTED'),
            action: deleteSelected,
            isDeleteAction: true
        }
    ];

    const openDetails = (row: IVaultPasswordRow) => () => {
        openPasswordCallback(row.Id, row.VaultId, row.DisplayName);
    };

    return (
        <>
            <ModalComponent localModalSwitcher={openUpdatePasswordModal} modalSwitcherCallback={handleModalCloseCallback} {...modalData} />
            <PasslyTable
                rows={rows}
                setRows={setRows}
                setFilters={setFilters}
                order={order}
                orderBy={'DisplayName'}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={passwordsCount}
                onFetch={fetchMoreData}
                withCheckboxes={permissions.has(vaultUpdate) || permissions.has(vaultDelete)}
                emptyListHelper={translation('VAULT_PASSWORDS_EMPTY_LIST')}
                loadingData={loading}
                openDetails={openDetails}
                deleteRow={permissions.has(vaultDelete) ? deleteRow : undefined}
                resetLocal={reset}
                actionMenuItems={permissions.has(vaultDelete) ? actionMenuItems : undefined}
            />
        </>
    );
};

export default PasswordListComponent;
